<template>
  <div
    ref="root"
    :class="{ 'dropdown-container': true }"
    :tabindex="tabIndex"
    @keydown="handleKeyDown"
  >
    <div
      :class="{ header: true, 'header-open': isOpen }"
      @click="toggleDropdown"
    >
      <span v-if="selectedOptions.length === 0" class="title placeholder">
        {{ placeholder }}
      </span>
      <span
        v-else-if="
          allowMultiple &&
          displaySelectAllOption &&
          selectedOptions.length == options.length
        "
        v-tooltip="selectedOptionsStr"
        class="title"
      >
        {{ allSelectedLabel }}
      </span>
      <span
        v-else
        v-tooltip="selectedOptionsStr"
        class="title selection-summary"
      >
        {{ selectedOptionsStr }}
      </span>
      <icon-chevron-down
        :class="{ 'icon-chevron-up': isOpen, 'icon-chevron': true }"
      />
    </div>
    <Teleport to="body" :disabled="!expandDropdownWidth">
      <ul
        ref="panel"
        :style="expandDropdownWidth && isOpen ? panelStyle : undefined"
        :class="{
          'is-open': isOpen,
          'is-closed': !isOpen,
          dropdown: true,
          'expand-open-width': expandDropdownWidth,
        }"
      >
        <li
          v-if="allowMultiple && displaySelectAllOption"
          class="option"
          @click="onClickSelectAll(!selectAll)"
        >
          <span class="option-item">
            <div class="center-item">
              <input
                :tabindex="isOpen ? tabIndex : -1"
                type="checkbox"
                :checked="selectAll"
              />
            </div>
            <span class="text"> Select all </span></span
          >
        </li>
        <li v-for="option in options" :key="option[trackBy]" class="option">
          <span class="option-item" @click="onSelect(option)">
            <div class="center-item">
              <input
                v-if="allowMultiple"
                :tabindex="isOpen ? tabIndex : -1"
                type="checkbox"
                :checked="isSelected(option)"
              />
              <input
                v-else
                :tabindex="isOpen ? tabIndex : -1"
                class="radio-btn"
                type="radio"
                :checked="isSelected(option)"
              />
            </div>
            <span
              v-tooltip="expandDropdownWidth ? option[label] : undefined"
              class="text"
              >{{ option[label] }}
            </span></span
          >
        </li>
      </ul>
    </Teleport>
  </div>
</template>
<script setup lang="ts" generic="T extends Option">
import IconChevronDown from "@/components/icons/IconChevronDown.vue";
import { computed, onMounted, onUnmounted, ref, toRefs, watch } from "vue";
import type Option from "@/models/dropdownOption.model.ts";

const root = ref<HTMLElement | null>(null);
const panel = ref<HTMLElement | null>(null);
const panelStyle = ref<Record<string, string>>({});
const isOpen = ref(false);
const toggleDropdown = () => {
  if (options.value.length === 0) return;
  isOpen.value = !isOpen.value;
};

const updatePanelPosition = () => {
  if (!root.value || !expandDropdownWidth.value || !isOpen.value) return;
  const rect = root.value.getBoundingClientRect();
  const rightGutter = 20; // 2rem at the codebase's 10px root
  // rem not used for panelStyle as `getBoundingClientRect` & `innerWidth` return results in pixels
  panelStyle.value = {
    position: "fixed",
    top: `${rect.bottom}px`,
    left: `${rect.left}px`,
    minWidth: `${rect.width}px`,
    maxWidth: `${window.innerWidth - rect.left - rightGutter}px`,
  };
};
const selectedOptions = ref<T[]>([]);
const selectAll = ref<boolean>(false);
const props = withDefaults(
  defineProps<{
    options: T[];
    placeholder?: string;
    trackBy: string;
    label: string;
    allowMultiple: boolean;
    modelValue: T[];
    tabIndex?: number;
    allowEmpty?: boolean;
    allSelectedLabel?: string;
    displaySelectAllOption?: boolean;
    expandDropdownWidth?: boolean;
  }>(),
  {
    allowMultiple: true,
    tabIndex: 0,
    placeholder: "",
    allowEmpty: true,
    allSelectedLabel: "",
    displaySelectAllOption: false,
    expandDropdownWidth: false,
  },
);
const {
  placeholder,
  trackBy,
  label,
  options,
  modelValue,
  allowEmpty,
  allSelectedLabel,
  displaySelectAllOption,
  expandDropdownWidth,
} = toRefs(props);

const emits = defineEmits<{
  (e: "onSelect", selected: T[]): void;
}>();

const onClickSelectAll = (value: boolean) => {
  selectedOptions.value = value ? options.value : [];
  emits("onSelect", selectedOptions.value as T[]);
  selectAll.value = value;
};

const onSelect = (item: T) => {
  if (!props.allowMultiple) {
    if (allowEmpty.value && isSelected(item)) {
      selectedOptions.value = [];
    } else {
      selectedOptions.value = [item];
    }
    emits("onSelect", selectedOptions.value as T[]);
    return;
  }

  if (isSelected(item)) {
    if (!allowEmpty.value && selectedOptions.value.length == 1) return;
    selectedOptions.value = selectedOptions.value.filter(
      (op) => op[trackBy.value] !== item[trackBy.value],
    );
  } else {
    selectedOptions.value = [...selectedOptions.value, item] as T[];
  }

  emits("onSelect", selectedOptions.value as T[]);
};

const selectedOptionsStr = computed(() => {
  return selectedOptions.value
    .map((op) => op[label.value])
    .sort()
    .join(", ");
});

const isSelected = (option: T) => {
  return selectedOptions.value
    .map((op) => op[trackBy.value])
    .includes(option[trackBy.value]);
};

watch(
  modelValue,
  (value) => {
    selectedOptions.value = [...value];
    selectAll.value = selectedOptions.value.length == options.value.length;
  },
  { immediate: true },
);

const handleKeyDown = (event: KeyboardEvent) => {
  if (event.key === "Tab" && isOpen.value) {
    isOpen.value = false;
  } else {
    isOpen.value = true;
  }
};

const handleClickOutside = (evt: MouseEvent) => {
  const eventPath = evt.composedPath();
  const inRoot = root.value && eventPath.includes(root.value);
  const inPanel = panel.value && eventPath.includes(panel.value);
  if (!inRoot && !inPanel) {
    isOpen.value = false;
  }
};

watch(isOpen, (open) => {
  if (open) updatePanelPosition();
});

onMounted(() => {
  window.addEventListener("click", handleClickOutside);
  window.addEventListener("resize", updatePanelPosition);
  window.addEventListener("scroll", updatePanelPosition, true);
});

onUnmounted(() => {
  window.removeEventListener("click", handleClickOutside);
  window.removeEventListener("resize", updatePanelPosition);
  window.removeEventListener("scroll", updatePanelPosition, true);
});
</script>
<style scoped>
.header {
  display: grid;
  grid-template-columns: minmax(0, 5fr) 0.5fr;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
  cursor: pointer;
  border: 1px solid var(--vt-c-purple-2);
  border-radius: 0.8rem;
  height: 100%;
}

.header-open {
  border-radius: 0.8rem 0.8rem 0 0;
}

.dropdown-container {
  background: var(--vt-c-white);
  width: 100%;
  height: 100%;
  border-radius: 0.8rem;
}

.title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--vt-c-purple-3);
  font-size: 1.4rem;
  font-style: normal;
  font-weight: 600;
  line-height: 3.2rem;
}

.is-open {
  height: auto;
  position: absolute;
  background: var(--vt-c-white);
  /*
  1st layer: 10;
  2nd layer: 100;
  3rd layer: 1000;
  */
  z-index: 100;
  border: 1px solid var(--vt-c-purple-2);
  border-radius: 0 0 0.8rem 0.8rem;
  max-height: 26rem;
  overflow-y: auto;
}

.is-closed {
  height: 0;
  overflow: hidden;
}

.dropdown {
  width: 100%;
  transition: height 0.3s;
  padding: 0 1rem;
  position: absolute;
}

.dropdown.expand-open-width.is-open {
  width: max-content;
}

.dropdown.expand-open-width .option-item .text {
  max-width: 40rem;
}

.option-item {
  display: grid;
  grid-template-columns: auto 5fr;
  gap: 1rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.4rem;
  user-select: none;
  align-items: start;
}

.option-item .text {
  color: var(--vt-c-black-mute-1);
  font-size: 1.4rem;
  font-style: normal;
  font-weight: 500;
  line-height: 2.2rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.radio-btn {
  width: 2rem;
  height: 2rem;
}

input[type="checkbox"] {
  width: 1.6rem;
  height: 1.6rem;
}

.center-item {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.option {
  margin-top: 1rem;
  margin-bottom: 0.5rem;
}

.option-item:hover {
  background: var(--color-background-btn-default);
}

input[type="checkbox"] {
  accent-color: var(--vt-c-purple-4);
}

.icon-chevron {
  transition: transform 0.3s;
  stroke: var(--vt-c-purple-3);
}

.icon-chevron-up {
  transform: rotate(180deg);
}

.placeholder {
  color: var(--vt-c-grey-10);
}

.label {
  color: var(--vt-c-black-mute-1);
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  margin-bottom: 1rem;
  display: inline-block;
}
</style>
